/* import __COLOCATED_TEMPLATE__ from './zendesk-deprecation.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type Folder from 'embercom/models/content-service/folder';
import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import type ArticleSyncSetting from 'embercom/models/articles/article-sync-setting';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import type AppService from 'embercom/services/app-service';

interface Args {
  folder?: Folder;
}

interface Signature {
  Args: Args;
}

export default class ZendeskDeprecation extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare permissionsService: any;
  @service declare notificationsService: any;

  @tracked showMigrateModal = false;
  @tracked migrateModalView: any = null;

  get shouldShow(): boolean {
    if (
      !this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-deprecation-banner')
    ) {
      return false;
    }
    if (this.args.folder) {
      return this.isFolderDeprecated;
    }
    return this.deprecatedSources.length > 0;
  }

  private get isFolderDeprecated(): boolean {
    let folder = this.args.folder;
    return (
      folder?.isSynced === true &&
      !folder?.parentFolder &&
      folder?.sourceTypeName === 'zendesk' &&
      !folder?.source
    );
  }

  get deprecatedSources(): SyncSourceWrapper[] {
    let sources = this.store.peekAll('knowledge-hub/sync-source-wrapper');
    return sources.filter((source: SyncSourceWrapper) => {
      if (source.sourceType !== 'zendesk' || !source.sourceId) {
        return false;
      }
      return !this.store.peekRecord('import-service/import-source', source.sourceId);
    });
  }

  @action
  async openMigrateModal() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // permissionsService.checkPermission displays its own error modal
      return;
    }

    let syncSource = this.args.folder ? this.deprecatedSourceForFolder : this.deprecatedSources[0];

    if (!syncSource) {
      return;
    }

    try {
      let syncSettings = await this.store.findAll('articles/article-sync-setting');
      let syncSetting = syncSettings.find(
        (setting: ArticleSyncSetting) => setting.id === syncSource.sourceId.toString(),
      );

      if (!syncSetting || !syncSetting.siteUrl) {
        throw new Error('Sync setting not found or missing site URL');
      }

      this.migrateModalView = {
        title: this.intl.t('knowledge-hub.sync-modal.zendesk.migration-title'),
        component: 'knowledge-hub/setup-modal/sync-modal/zendesk',
        isMigrating: true,
        existingSyncSetting: syncSetting,
      };

      this.showMigrateModal = true;
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.zendesk.migration-failed'),
      );
    }
  }

  @action
  closeMigrateModal() {
    this.showMigrateModal = false;
    this.migrateModalView = null;
  }

  private get deprecatedSourceForFolder(): SyncSourceWrapper | undefined {
    if (!this.args.folder?.sourceId) {
      return undefined;
    }
    let sources = this.store.peekAll('knowledge-hub/sync-source-wrapper');
    return sources.find(
      (source: SyncSourceWrapper) =>
        source.sourceType === 'zendesk' &&
        source.sourceId?.toString() === this.args.folder!.sourceId?.toString(),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Banners::ZendeskDeprecation': typeof ZendeskDeprecation;
    'knowledge-hub/banners/zendesk-deprecation': typeof ZendeskDeprecation;
  }
}
